import { Box, Button, ButtonGroup, Grid, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { Fragment, useState } from "react";
import { getTranslation } from "../../utils/translate";
import useSWR from "swr";
import { fetcher } from "../../common/utils/fetcher";
import KeyValueCard from "../KeyValueCard";
import BarChart from "../shared/BarChart";
import { chooseInputComponent } from "../../common/utils/components";
import { getUrlParams } from "../../common/utils/url";
import DataVisualiser from "../DataVisualiser";

const PageViewInsights = () => {
    const retailer = useSelector((state) => state.retailer);
    const [contestId, setContestId] = useState('cd992816-30d5-40c9-8092-0755c8eff0cd');
    const [recipeId, setRecipeId] = useState('ab9f2eee-f9e5-4c65-9564-bcd0fcd0e9ce');
    const [selectedCountry, setSelectedCountry] = useState('CA');
    const [selectedProvince, setSelectedProvince] = useState('ON');
    const [selectedRetailer, setSelectedRetailer] = useState('CA');

    const { data: contestData } = useSWR({
        url: 'contests',
        fields: 'id,achievements.*',
        filter: {
            _and: [
                {
                    id: {
                        _eq: contestId,
                    }
                },
            ],
        },
    }, fetcher);

    let contest = null;

    if (contestData) {
        contest = contestData[0];
    }

    const { data: thisYearRecipesPageViewsData } = useSWR({
        url: 'page_views',
        filter: {
            _and: [
                {
                    date_created: {
                        _gte: '2025-01-01',
                    }
                },
                {
                    url: {
                        _contains: '/recipe/',
                    }
                },
            ],
        },
        aggregate: {
            count: '*',
        },
        groupBy: ['url'],
        limit: 100,
        sort: ['-count'],
    }, fetcher);

    let thisYearRecipesPageViews = null;

    if (thisYearRecipesPageViewsData) {
        thisYearRecipesPageViews = thisYearRecipesPageViewsData;
    }



    const { data: thisYearRecipesRetailersPageViewsData } = useSWR({
        url: 'page_views',
        filter: {
            _and: [
                {
                    date_created: {
                        _gte: '2025-01-01',
                    }
                },
                {
                    url: {
                        _contains: '/recipe/',
                    }
                },
            ],
        },
        aggregate: {
            count: '*',
        },
        groupBy: ['retailer'],
        limit: -1,
        sort: ['-count'],
    }, fetcher);

    let thisYearRecipesRetailersPageViews = null;

    if (thisYearRecipesRetailersPageViewsData) {
        thisYearRecipesRetailersPageViews = thisYearRecipesRetailersPageViewsData;
    }


    const { data: thisYearPrimalBeefPageViewsData } = useSWR({
        url: 'page_views',
        filter: {
            _and: [
                {
                    date_created: {
                        _gte: '2025-01-01',
                    }
                },
                {
                    url: {
                        _contains: '/primal/',
                    }
                },
            ],
        },
        aggregate: {
            count: '*',
        },
        groupBy: ['url'],
        limit: -1,
        sort: ['-count'],
    }, fetcher);

    let thisYearPrimalBeefPageViews = null;

    if (thisYearPrimalBeefPageViewsData) {
        thisYearPrimalBeefPageViews = thisYearPrimalBeefPageViewsData;
    }

    const { data: achievementsData } = useSWR({
        url: 'points',
        filter: {
            _and: [
                {
                    contest: {
                        _eq: contestId,
                    }
                },
            ],
        },
        aggregate: {
            count: '*',
        },
        groupBy: 'achievement',
    }, fetcher);

    let achievements = null;

    if (achievementsData) {
        achievements = achievementsData;
    }

    const { data: provincesData } = useSWR({
        url: 'page_views',
        aggregate: {
            count: '*',
        },
        groupBy: ['country', 'region'],
        sort: ['-count'],
        limit: -1,
    }, fetcher);

    let provinces = null;

    if (provincesData) {
        provinces = provincesData;
    }

    const { data: countriesData } = useSWR({
        url: 'page_views',
        aggregate: {
            count: '*',
        },
        groupBy: ['country'],
        sort: ['-count'],
        limit: 10,
    }, fetcher);

    let countries = null;

    if (countriesData) {
        countries = countriesData;
    }

    const { data: utmMediumsData } = useSWR({
        url: 'page_views',
        aggregate: {
            count: '*',
        },
        filter: {
            _and: [
                {
                    url: {
                        _contains: 'utm_',
                    }
                },
            ],
        },
        groupBy: ['url'],
        sort: ['-count'],
        limit: -1,
    }, fetcher);

    let utmMedium = null;
    let utmSource = null;
    let utmCampaign = null;

    if (utmMediumsData) {
        console.log('utmMediumutmMediumsData: ', utmMediumsData);

        // Group by utmMedium and sum the counts
        const groupedMedium = utmMediumsData.reduce((acc, item) => {
            const utmMedium = getUrlParams(item.url, 'utm_medium');
            if (utmMedium) {
                // Clean up UTM medium: trim whitespace and convert to uppercase
                const cleanUtmMedium = utmMedium.trim().toUpperCase();
                if (acc[cleanUtmMedium]) {
                    acc[cleanUtmMedium] += item.count;
                } else {
                    acc[cleanUtmMedium] = item.count;
                }
            }
            return acc;
        }, {});

        // Convert grouped object back to array format for the chart
        utmMedium = Object.entries(groupedMedium).map(([utmMedium, count]) => ({
            utmMedium,
            count
        }));

        // Group by utmSource and sum the counts
        const groupedSource = utmMediumsData.reduce((acc, item) => {
            const utmSource = getUrlParams(item.url, 'utm_source');
            if (utmSource) {
                // Clean up UTM source: trim whitespace and convert to uppercase
                const cleanUtmSource = utmSource.trim().toUpperCase();
                if (acc[cleanUtmSource]) {
                    acc[cleanUtmSource] += item.count;
                } else {
                    acc[cleanUtmSource] = item.count;
                }
            }
            return acc;
        }, {});

        // Convert grouped object back to array format for the chart
        utmSource = Object.entries(groupedSource).map(([utmSource, count]) => ({
            utmSource,
            count
        }));

        // Group by utmCampaign and sum the counts
        const groupedCampaign = utmMediumsData.reduce((acc, item) => {
            const utmCampaign = getUrlParams(item.url, 'utm_campaign');
            if (utmCampaign) {
                // Clean up UTM campaign: trim whitespace and convert to uppercase
                const cleanUtmCampaign = utmCampaign.trim().toUpperCase();
                if (acc[cleanUtmCampaign]) {
                    acc[cleanUtmCampaign] += item.count;
                } else {
                    acc[cleanUtmCampaign] = item.count;
                }
            }
            return acc;
        }, {});

        // Convert grouped object back to array format for the chart
        utmCampaign = Object.entries(groupedCampaign).map(([utmCampaign, count]) => ({
            utmCampaign,
            count
        }));
    }

    console.log('utmMedium: ', utmMedium);
    console.log('utmSource: ', utmSource);
    console.log('utmCampaign: ', utmCampaign);

    const { data: registrationsData } = useSWR({
        url: 'points',
        filter: {
            _and: [
                {
                    contest: {
                        _eq: contestId,
                    }
                },
                {
                    
                }
            ],
        },
        aggregate: {
            count: '*',
        },
    }, fetcher);

    const { data: newsletterSubscriptionsData } = useSWR({
        url: 'points',
        filter: {
            _and: [
                {
                    contest: {
                        _eq: contestId,
                    }
                },
            ],
        },
        deep: {
            _filter: {
                short_code: {
                    receive_updates: {
                        _eq: true,
                    }
                }
            }
        },
        aggregate: {
            count: '*',
        },
    }, fetcher);

    let registrationsCount = 0;
    let newsletterSubscriptionsCount = 0;

    if (registrationsData) {
        registrationsCount = registrationsData[0].count;
    }

    if (newsletterSubscriptionsData) {
        newsletterSubscriptionsCount = newsletterSubscriptionsData[0].count;
    }

    const formSections = [  
        {
            title: '',
            fields: [
                {
                    label: 'Contest',
                    name: 'contest_id',
                    type: 'm2o',
                    value: contestId,
                    handleInputChange: (value) => {
                        setContestId(value);
                    },
                    config: {
                        size: '12',
                        translations: [],
                        title: 'Contests',
                        url: '/contests',
                        searchOnly: 'CONTESTS',
                        detailsPayload: {
                            fields: '*,translations.*',
                        },
                        detailsUrl: `/contests`,
                        detailsResponseHandler: (data) => {
                            const translatedItem = getTranslation(data, { key: 'languages_code', code: 'en-US' })

                            return {
                                id: data?.id,
                                title: translatedItem?.tagline || "",
                                description: translatedItem?.heading || "",
                                imageUrl: `${process.env.REACT_APP_API_URL}/assets/${translatedItem?.featured_image}?key=brand-portal-image-card-square`,
                                url: `/contests/${data.id}`,
                                data,
                            };
                        },
                        payload: {
                            filter: {
                                '_or': [
                                    {
                                        retailers: {
                                            retailer_id: {
                                                '_eq': retailer.id
                                            }
                                        }
                                    },
                                ]
                            },
                            fields: '*, translations.*',
                            limit: 25,
                            meta: '*',
                        },
                        rows: [],
                        responseHandler: (data) => {
                            return data?.data?.map((item) => {
                                const translatedItem = getTranslation(item, { key: 'languages_code', code: 'en-US' })
                                return {
                                    id: item?.id,
                                    title: translatedItem?.tagline || "",
                                    description: translatedItem?.heading || "",
                                    imageUrl: `${process.env.REACT_APP_API_URL}/assets/${translatedItem?.featured_image}`,
                                    url: `/contests/${item.id}`,
                                    data: item,
                                };
                            }) || [];
                        },
                        headCells: [],
                        items: [],
                    },
                }, 
            ],
        },
    ];

    return (
        <Box>
            {/* <Box>
                {formSections.map((section) => (
                    <Box key={section.title}>
                        {section.title && (
                            <Typography
                                className="form-section-title"
                                variant="h4"
                            >
                                {section.title}
                            </Typography>
                        )}

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={7}>
                                <Box sx={{ flexGrow: 1 }}>
                                    {section.fields.map((field) => (
                                        <Fragment key={field.name}>
                                            <Grid
                                                key={field.name}
                                                item
                                                {...(["colour"].includes(field?.type)
                                                    ? {
                                                        xs: 12,
                                                        md: 4,
                                                    }
                                                    : {
                                                        xs: 12,
                                                    })}
                                            >
                                                {chooseInputComponent(field, 'en-US')}
                                            </Grid>
                                        </Fragment>
                                    ))}
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                ))}
            </Box> */}

            {/* <Grid container
                spacing={2}
            >
                {
                    achievements?.map((achievement) => (
                        <Grid
                            key={achievement.achievement}
                            item
                            xs={12}
                            md={4}
                            lg={3}
                        >
                            <KeyValueCard
                                label={achievement.achievement}
                                variant="statistic"
                                value={achievement.count}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                }}
                                dynamicLabel={{
                                    detailsPayload: {
                                        fields: '*,translations.*',
                                    },
                                    detailsUrl: `achievements/${achievement.achievement}`,
                                    detailsResponseHandler: (data) => {
                                        const translatedItem = getTranslation(data, { key: 'languages_code', code: 'en-US' })

                                        return {
                                            id: data?.id,
                                            title: translatedItem?.title || "",
                                        };
                                    },
                                }}
                            />
                        </Grid>
                    ))
                }
            </Grid> */}

            <Box>
                <ButtonGroup
                    sx={{
                        my: 2,
                    }}
                >
                    {countries?.filter((country) => country.country !== 'ZW' && country.country !== null)?.map((country) => (
                        <Button key={country.country} 
                            onClick={() => {
                                setSelectedCountry(country.country);
                            }}
                            variant={selectedCountry === country.country ? 'contained' : 'outlined'}
                            size="small"
                            sx={{
                                textTransform: 'none',
                            }}
                        >
                            {country.country}
                        </Button>
                    ))}
                </ButtonGroup>

                <DataVisualiser
                    data={provinces?.filter((province) => province.country === selectedCountry)}
                    xKey="region"
                    yKey="count"
                    xLabel="Provinces"
                    yLabel="Count"
                    title="Provinces Page Views"
                    description="Provinces Page Views"
                    horizontal={true}
                />
            </Box>

            <Box>
                <DataVisualiser
                    data={utmMedium}
                    xKey="utmMedium"
                    yKey="count"
                    xLabel="UTM Medium"
                    yLabel="Count"
                    title="UTM Medium Page Views"
                    description="UTM Medium Page Views"
                    horizontal={true}
                />
            </Box>

            <Box>
                <DataVisualiser
                    data={utmSource}
                    xKey="utmSource"
                    yKey="count"
                    xLabel="UTM Source"
                    yLabel="Count"
                    title="UTM Source Page Views"
                    description="UTM Source Page Views"
                    horizontal={true}
                />
            </Box>

            <Box>
                <DataVisualiser
                    data={utmCampaign}
                    xKey="utmCampaign"
                    yKey="count"
                    xLabel="UTM Campaign"
                    yLabel="Count"
                    title="UTM Campaign Page Views"
                    description="UTM Campaign Page Views"
                    horizontal={true}
                />
            </Box>
        </Box >
    )
}

export default PageViewInsights